/**
 * Naming the file an action makes.
 *
 * Keeping or removing rows, removing duplicates and suppressing each make a
 * new file and leave the one they started from exactly as it was. These are
 * the fields every one of those dialogs asks for, and what happens after:
 * the new file opens, and a note says the original is still there.
 */
import { useEffect, useState } from 'react';
import { Alert, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { formatNumber } from '../theme';

/** A file name without its .csv, for building a new one from it. */
export function fileStem(name: string): string {
  return name.replace(/\.csv$/i, '').trim();
}

/** Name and description state, reset to fresh suggestions each time it opens. */
export function useNewFileDetails(opened: boolean, suggestName: string, suggestDescription: string) {
  const [name, setName] = useState(suggestName);
  const [description, setDescription] = useState(suggestDescription);
  // Only when the dialog opens: refreshing the suggestion while someone is
  // typing would overwrite what they wrote.
  useEffect(() => {
    if (opened) {
      setName(suggestName);
      setDescription(suggestDescription);
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps
  return { name, setName, description, setDescription };
}

export function NewFileFields({
  details,
  sourceName,
}: {
  details: ReturnType<typeof useNewFileDetails>;
  sourceName: string;
}) {
  return (
    <Stack gap="xs">
      <Alert color="gray" variant="light" p="xs">
        <Text size="sm">
          This makes a <b>new file</b>. <b>{sourceName}</b> is left exactly as it is.
        </Text>
      </Alert>
      <TextInput
        label="New file name"
        value={details.name}
        onChange={(e) => details.setName(e.currentTarget.value)}
      />
      <Textarea
        label="Description"
        description="What this file is, so it makes sense in the Files list later"
        autosize
        minRows={2}
        value={details.description}
        onChange={(e) => details.setDescription(e.currentTarget.value)}
      />
    </Stack>
  );
}

/** After an action makes a file: open it and say what happened. */
export function useOpenNewFile() {
  const navigate = useNavigate();
  return (made: { id: string; file_name: string; row_count: number }, sourceName: string) => {
    notifications.show({
      color: 'teal',
      title: `Saved as ${made.file_name}`,
      message: `${formatNumber(made.row_count)} rows. ${sourceName} is unchanged and still in Files.`,
    });
    navigate(`/sheets/${made.id}`);
  };
}
